if (!customElements.get('quick-add-sizes')) {
  customElements.define(
    'quick-add-sizes',
    class QuickAddSizes extends HTMLElement {
      constructor() {
        super();

        this.onPageScroll = () => this.closeMenus();

        // Delegated listeners, attached on the host itself, so they work no matter when the
        // browser parsed the children. scroll / toggle don't bubble, hence the capture phase.
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('scroll', (event) => this.onScroll(event), true);
        this.addEventListener('toggle', (event) => this.onToggle(event), true);
        this.addEventListener('submit', (event) => this.onSubmit(event));
      }

      connectedCallback() {
        const setup = () => {
          const track = this.track;
          if (!track) return;

          this.resizeObserver?.disconnect();
          this.resizeObserver = new ResizeObserver(() => this.update());
          this.resizeObserver.observe(track);
          document.fonts?.ready.then(() => this.update());
          this.update();
          requestAnimationFrame(() => this.update());
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', setup, { once: true });
        } else {
          setup();
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.removeEventListener('scroll', this.onPageScroll);
      }

      get track() {
        return this.querySelector('.quick-add-sizes__track');
      }

      /* ---------- carousel ---------- */

      update() {
        const track = this.track;
        if (!track) return;

        const prev = this.querySelector('[data-direction="prev"]');
        const next = this.querySelector('[data-direction="next"]');
        const max = track.scrollWidth - track.clientWidth;

        this.toggleAttribute('data-overflow', max > 1);
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
      }

      scrollPage(direction) {
        const track = this.track;
        if (!track) return;
        track.scrollBy({ left: direction * track.clientWidth * 0.75, behavior: 'smooth' });
      }

      onScroll(event) {
        if (event.target !== this.track) return;
        this.closeMenus();
        this.update();
      }

      /* ---------- "M / M Tall" dropdown (native popover) ---------- */

      isOpen(menu) {
        try {
          return menu.matches(':popover-open');
        } catch (error) {
          return false;
        }
      }

      closeMenus() {
        this.querySelectorAll('.quick-add-sizes__menu').forEach((menu) => menu.hidePopover?.());
      }

      onClick(event) {
        const arrow = event.target.closest('[data-direction]');
        if (arrow) {
          this.scrollPage(arrow.dataset.direction === 'next' ? 1 : -1);
          return;
        }

        const toggle = event.target.closest('.quick-add-sizes__button--toggle');
        if (!toggle) return;

        const menu = document.getElementById(toggle.getAttribute('popovertarget'));
        if (!menu || this.isOpen(menu)) return;

        // The popover opens right after this click handler finishes. Give it a position now so it
        // never shows up at the viewport's top-left corner, then fine-tune once it has a size.
        const rect = toggle.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.left = `${rect.left}px`;
        requestAnimationFrame(() => this.placeMenu(menu, toggle));
      }

      placeMenu(menu, toggle) {
        if (!toggle || !this.isOpen(menu)) return;

        const gap = 4;
        const edge = 8;
        const toggleRect = toggle.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        // keep it inside the viewport horizontally
        const left = Math.max(edge, Math.min(toggleRect.left, window.innerWidth - menuRect.width - edge));

        // under the button, or above it when there is no room below
        let top = toggleRect.bottom + gap;
        const flippedTop = toggleRect.top - menuRect.height - gap;
        if (top + menuRect.height > window.innerHeight - edge && flippedTop > edge) top = flippedTop;

        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
      }

      onToggle(event) {
        const menu = event.target;
        if (!menu.classList || !menu.classList.contains('quick-add-sizes__menu')) return;

        const toggle = menu.previousElementSibling;
        const isOpen = event.newState === 'open';
        toggle?.setAttribute('aria-expanded', String(isOpen));

        if (isOpen) {
          this.placeMenu(menu, toggle);
          // the dropdown is fixed to the viewport, so close it if the page scrolls away from the button
          window.addEventListener('scroll', this.onPageScroll, { passive: true });
        } else {
          window.removeEventListener('scroll', this.onPageScroll);
        }
      }

      onSubmit(event) {
        // close the dropdown as soon as a size is chosen
        const menu = event.target.closest('.quick-add-sizes__menu');
        menu?.hidePopover?.();
      }
    }
  );
}